import Portal from "@client/controls/Portal/component";
import Draggable, { type DraggableHOCComponentProps, type DraggableHOCProps } from "@client/hocs/draggable";
import StickableHOC, { type StickActionsProps, type StickSide, type StickableOptionalProps } from "@client/hocs/stickable";
import { getStickSide } from "@client/hocs/stickable/lib";
import { type LeftTop, type Mode } from "@client/types";
import { type ViewportAppState } from "@client/types/AppState/viewport";
import { AllowOffScreen } from "@common/hocs/draggable/lib";
import classnames from "classnames";
import { isNull, isUndefined } from "lodash";
import * as React from "react";
import Body from "./Body/component";
import Content, { type ContentChildren } from "./Content/component";
import Footer from "./Footer/component";
import Header, { type HeaderProps } from "./Header/component";
import { getInitialPosition } from "./lib";

import "./style.less";

export interface ContentWrapProps {
  children: any;
  height?: number | string;
  isDraggable: boolean;
  isFullScreen?: boolean;
  isLoading?: boolean;
  isSticked?: boolean;
  maxHeight?: string | number;
  onBack?: () => void;
  onClose?: () => void;
  onFullScreen?: () => void;
  onResized?: (width: number) => void;
  style?: React.CSSProperties;
  toStickSide: StickSide | null;
}
class ContentWrap extends React.Component<ContentWrapProps & StickableOptionalProps> {
  public render() {
    return (
      <Content
        isLoading={this.props.isLoading}
        height={this.props.height}
        maxHeight={this.props.maxHeight}
        style={this.props.style}
      >
        {this._convertChildren()}
      </Content>
    );
  }

  private _convertChildren(): ContentChildren {
    const children = React.Children.toArray(this.props.children);

    const header = children.find((ch) => (ch as React.ReactElement<any>).type === Modal.Header) || null;
    const body = children.find((ch) => (ch as React.ReactElement<any>).type === Modal.Body) || null;
    const footer = children.find((ch) => (ch as React.ReactElement<any>).type === Modal.Footer) || null;

    return {
      header: this._wrapHeader(header as React.ReactElement<HeaderProps>),
      body,
      footer
    };
  }

  private _wrapHeader = (header: React.ReactElement<HeaderProps> | null): React.ReactElement<HeaderProps> | null => {
    if (!header) return null;

    return React.cloneElement(header, {
      isFullScreen: this.props.isFullScreen,
      onBackClick: this.props.onBack,
      onCloseClick: this.props.onClose,
      onStickClick: this.props.onStickClick,
      onFullScreenClick: this.props.onFullScreen,
      toStickSide: this.props.toStickSide
    });
  };
}

type DialogProps = ContentWrapProps &
  React.HTMLProps<HTMLDivElement> &
  StickableOptionalProps &
  Partial<DraggableHOCComponentProps> & {
    minWidth?: string | number;
    maxWidth?: string | number;
    width?: string | number;
  };
const Dialog = React.forwardRef((props: DialogProps, ref: React.RefObject<HTMLDivElement>) => {
  React.useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;

      const start = e.x;
      let lastX = start,
        initialLeft = parseInt(element.style.left),
        initialWidth = parseInt(element.style.width),
        minWidth = element.style.minWidth ? parseInt(element.style.minWidth) : 0,
        maxWidth = element.style.minWidth ? parseInt(element.style.maxWidth) : window.innerWidth;

      const handle = (isLeft: boolean) => {
        document.body.style.userSelect = "none";

        const onStop = (e: MouseEvent) => {
          document.body.style.userSelect = "";

          props.onResized!(element.clientWidth);

          document.removeEventListener("mouseup", onStop);
          document.removeEventListener("mouseleave", onStop);
          document.removeEventListener("mousemove", onMouseMove);
        };

        const onMouseMove = (e: MouseEvent) => {
          if (Math.abs(lastX - e.x) > 1 || e.x !== start) {
            const diff = Math.round(isLeft ? lastX - e.x : e.x - lastX);
            const currentWidth = element.clientWidth;
            const width = currentWidth + diff;
            const newWidth = Math.max(minWidth, Math.min(maxWidth, width));

            if (Math.abs(newWidth - currentWidth) > 1) {
              if (isLeft) {
                element.style.left = `${initialLeft - newWidth + initialWidth}px`;
              }
              element.style.width = `${newWidth}px`;
            }

            lastX = e.x;
          }
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onStop);
        document.addEventListener("mouseleave", onStop);
      };

      if ((e.target as HTMLDivElement).classList.contains("left_handle")) {
        handle(true);
      }
      if ((e.target as HTMLDivElement).classList.contains("right_handle")) {
        handle(false);
      }
    };

    const element = ref.current!;
    element.addEventListener("mousedown", onMouseDown);

    return () => {
      element.removeEventListener("mousedown", onMouseDown);
    };
  }, [props.onResized, props.minWidth, props.maxWidth]);

  const {
    onStickClick,
    height,
    maxHeight,
    isLoading,
    children,
    onClose,
    onBack,
    onKeyDown,
    isDraggable,
    isDragging,
    isFullScreen,
    isSticked,
    onFullScreen,
    onResized,
    toStickSide,
    minWidth,
    maxWidth,
    type,
    id,
    width,
    ...rest
  } = props;

  return (
    <div ref={ref} {...rest} style={{ minWidth, maxWidth, width }}>
      {!!props.onStickClick && (!isSticked || toStickSide === "right") && <div className="left_handle" />}
      <ContentWrap
        toStickSide={toStickSide}
        isDraggable
        onBack={onBack}
        height={height}
        maxHeight={maxHeight}
        isLoading={isLoading}
        children={children}
        onClose={onClose}
        onStickClick={onStickClick}
      />
      {!!props.onStickClick && (!isSticked || toStickSide === "left") && <div className="right_handle" />}
    </div>
  );
});

const EnhancedDialog = StickableHOC<DialogProps & DraggableHOCProps>(
  Draggable<DialogProps & DraggableHOCProps & StickableOptionalProps>(Dialog as any, ".modal-header")
);

export type ModalCloseBehaviour = "cross" | "not-allowed" | "click-outside";
export type ModalInnerProps = Partial<StickActionsProps> & {
  children: any;
  className?: string;
  closeBehaviour?: ModalCloseBehaviour;
  containerWidth?: string | number;
  hasBackdrop?: boolean;
  height?: string | number;
  initialPosition?: LeftTop;
  isAdminColor?: boolean;
  isCentered?: boolean;
  isDraggable?: boolean;
  isFullScreenAllowed?: boolean;
  isLoading?: boolean;
  isHidden?: boolean;
  isSticked?: boolean;
  isTopMost?: boolean;
  maxHeight?: string | number;
  minTop?: number;
  minWidth?: string | number;
  maxWidth?: string | number;
  onBack?: () => void;
  onClose?: () => void;
  overlayColor?: string;
  stickSide?: StickSide | null;
  storageId?: string;
  style?: React.CSSProperties;
  width?: string | number;
};

type State = {
  isFullScreen: boolean;
  toStickSide: StickSide | null;
  width: string | number;
};
class ModalInner extends React.PureComponent<ModalInnerProps, State> {
  private _ref = React.createRef<HTMLDivElement>();

  public constructor(props: ModalInnerProps) {
    super(props);

    this.state = {
      isFullScreen: false,
      toStickSide: props.stickSide ?? null,
      width: props.width ?? 300
    };
  }

  public componentDidMount() {
    if (this.props.closeBehaviour === "click-outside" && !this.props.hasBackdrop) {
      document.addEventListener("mousedown", this._onClickOutside, true);
    }
  }

  public componentDidUpdate(prevProps: Readonly<ModalInnerProps>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.width !== this.props.width) {
      this.setState({ width: this.props.width ?? 300 });
    }
  }

  public componentWillUnmount() {
    if (this.props.closeBehaviour === "click-outside" && !this.props.hasBackdrop) {
      document.removeEventListener("mousedown", this._onClickOutside, true);
    }
  }

  private _onClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    const dialog = this._ref.current!.querySelector(".modal-dialog");
    if (
      !dialog?.contains(target) &&
      !target.closest(".dropdown_container") &&
      !target.closest(".no-close-click-outside")
      // TODO: fix https://app.clickup.com/t/86c4p9uru. We cannot use .modal-dialog
      // !target.closest(".modal-dialog")
    ) {
      this.props.onClose && this.props.onClose();
    }
  };

  // TODO: process same way as stickSide
  private _onClose = () => this.props.closeBehaviour !== "not-allowed" && this.props.onClose && this.props.onClose();
  private _onDragged = (lt: LeftTop, e: MouseEvent) => {
    const toStickSide = this.props.stickSide ?? getStickSide({ left: e.clientX, top: e.clientY });
    if (toStickSide !== this.state.toStickSide) {
      this.setState({ toStickSide });
    }
  };

  private _onResized = (width: number) => {
    this.setState({ width });
  };

  private _onFullScreen = () => {
    this.setState({ isFullScreen: !this.state.isFullScreen });
  };
  private _onStick = (stickSide: StickSide) => this._onStickChanged(stickSide);
  private _onUnstick = () => this._onStickChanged(null);

  private _onStickChanged = (stickSide: StickSide | null) => {
    if (stickSide) {
      this.props.onStick && this.props.onStick(stickSide);
    } else {
      this.props.onUnstick && this.props.onUnstick();
    }
  };

  private _onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") return;

    if (
      this.props.closeBehaviour !== "not-allowed" &&
      this.props.closeBehaviour !== "cross" &&
      !this.props.stickSide &&
      this.props.onClose
    ) {
      e.stopPropagation();
      this.props.onClose();
    }
  };
  private _onMouseDown = (e: React.MouseEvent) =>
    e.button === 0 &&
    !this.props.stickSide &&
    this.props.closeBehaviour !== "not-allowed" &&
    this.props.closeBehaviour !== "cross" &&
    (e.stopPropagation(), this._onClose());

  public render() {
    const {
      className,
      onClose,
      closeBehaviour,
      hasBackdrop,
      isAdminColor,
      isCentered,
      isDraggable,
      isFullScreenAllowed,
      isHidden,
      initialPosition,
      isSticked,
      isTopMost,
      maxWidth,
      minWidth,
      width,
      stickSide,
      onStick,
      onUnstick,
      storageId,
      overlayColor,
      ...contentProps
    } = this.props;

    const style: React.CSSProperties = {
      "--backdrop-color": overlayColor ?? "var(---modalBackdropColor)"
    } as any;

    return (
      <div
        ref={this._ref}
        className={classnames("wsc_modal modal fade show in", className, {
          draggable: isDraggable,
          fullscreen: this.state.isFullScreen,
          hidden: isHidden,
          "no-backdrop": !hasBackdrop,
          "stick-left": isSticked && stickSide === "left",
          "stick-right": isSticked && stickSide === "right",
          "admin _theme_": isAdminColor,
          top_most: isTopMost
        })}
        onKeyDown={this._onKeyDown}
        onMouseDown={this._onMouseDown}
        tabIndex={0}
        role="dialog"
        style={style}
      >
        {isDraggable ? (
          <EnhancedDialog
            allowOffBounds={AllowOffScreen.Right | AllowOffScreen.Left}
            parentContainerSelector=".modal-content"
            className={classnames("modal-dialog")}
            boundsSelector=".modals_container"
            initialPosition={initialPosition ?? getInitialPosition(this.state.toStickSide, this.state.width)}
            isFullScreen={this.state.isFullScreen}
            stickSide={this.props.stickSide}
            onDragged={this._onDragged}
            onResized={this._onResized}
            onStick={isNull(this.props.stickSide) ? undefined : this._onStick}
            onUnstick={isNull(this.props.stickSide) ? undefined : this._onUnstick}
            onClose={this._onClose}
            onFullScreen={isFullScreenAllowed ? this._onFullScreen : undefined}
            isDraggable
            isSticked={isSticked}
            toStickSide={this.state.toStickSide}
            minWidth={minWidth}
            maxWidth={maxWidth}
            width={this.state.width}
            {...contentProps}
          />
        ) : (
          <div
            className={classnames("modal-dialog", { "modal-dialog-centered": isCentered })}
            style={{ maxWidth: this.state.width, minWidth, width: "100%" }}
          >
            <ContentWrap
              {...contentProps}
              toStickSide={this.state.toStickSide}
              isDraggable={false}
              isFullScreen={this.state.isFullScreen}
              onClose={this._onClose}
              onFullScreen={isFullScreenAllowed ? this._onFullScreen : undefined}
            />
          </div>
        )}
      </div>
    );
  }
}

export type ModalProps = Omit<ModalInnerProps, "containerWidth"> & {
  appType?: Mode;
  isInViewport?: boolean;
  isVisible: boolean;
  viewport?: ViewportAppState;
};

class Modal extends React.PureComponent<ModalProps> {
  public static defaultProps = {
    isAdminColor: false,
    isCentered: false,
    isLoading: false,
    hasBackdrop: true,
    isDraggable: false,
    stickSide: null
  };
  public static Header = Header;
  public static Body = Body;
  public static Footer = Footer;

  public render() {
    const { appType, className, isInViewport, isVisible, viewport, ...props } = this.props;
    if (!isVisible) return null;

    /* todo: add a separate property to specify the location (parentSelector) */
    const isAdmin = className && className.split(" ").includes("admin");
    const iiv = isUndefined(isInViewport) ? !isAdmin && (!appType || appType !== "viewer") : isInViewport;
    return (
      <Portal parentSelector={iiv ? ".wsc_viewport" : undefined}>
        <ModalInner className={className} {...props} />
      </Portal>
    );
  }
}

export default Modal;

export const InlineModal = ({ isVisible, ...props }: ModalProps) => {
  if (!isVisible) return null;

  return <ModalInner {...props} />;
};
